<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { useRouter } from 'vue-router';
import instance, { useMock, showError } from '@/utils/request';
import { useAuthStore } from '@/stores/auth';
import { useBrandStore } from '@/stores/brand';

const router = useRouter();
const auth = useAuthStore();
const brand = useBrandStore();
const logoStyle = computed(() => (brand.logo ? { backgroundImage: `url(${brand.logo})` } : {}));
const form = ref({ username: useMock ? 'admin' : '', password: useMock ? 'admin123' : '', captchaId: '', captcha: '' });
const loading = ref(false);
const captchaLoading = ref(false);
const captchaImage = ref('');

const refreshCaptcha = async () => {
  captchaLoading.value = true;
  try {
    const res = await instance.get('/admin/auth/captcha');
    form.value.captchaId = res.captchaId;
    form.value.captcha = '';
    captchaImage.value = res.image;
  } catch (err) {
    captchaImage.value = '';
    showError(err, '验证码加载失败');
  } finally {
    captchaLoading.value = false;
  }
};

const submit = async () => {
  // 防并发:回车(keyup.enter + 表单 submit)或双击会触发两次登录,第二次必报"验证码已过期"(验证码验后即删)
  if (loading.value) return;
  if (!form.value.username || !form.value.password || !form.value.captchaId || !form.value.captcha) {
    ElMessage.warning('请填写账号、密码和图形验证码');
    return;
  }
  loading.value = true;
  try {
    const res = await instance.post('/admin/auth/login', form.value);
    auth.setAuth({
      token: res.token,
      admin: {
        id: res.admin.id,
        username: res.admin.username,
        realName: res.admin.realName,
        role: res.admin.role,
        roleName: res.admin.roleName,
        avatar: res.admin.avatar,
        permissions: res.permissions,
      },
    });
    ElMessage.success('登录成功');
    router.push('/dashboard');
  } catch (err) {
    showError(err, '登录失败');
    await refreshCaptcha();
  } finally {
    loading.value = false;
  }
};

onMounted(refreshCaptcha);
</script>

<template>
  <div class="login-page">
    <div class="login-brand">
      <div class="brand-logo" :style="logoStyle" role="img" aria-label="系统 Logo" />
      <h2>让慢病服务更专业、更高效</h2>
      <p>连接会员、健康管理、内容运营与营养商城的一体化运营平台</p>
      <div class="feature-list"><span>✓ 健康档案管理</span><span>✓ 营养商品运营</span><span>✓ 全链路数据留痕</span></div>
    </div>
    <div class="login-box">
      <div class="login-logo" :style="logoStyle" role="img" aria-label="系统 Logo" />
      <h1>欢迎回来</h1>
      <p class="subtitle">登录{{ brand.mainTitle }}运营管理后台</p>
      <ElForm @submit.prevent="submit">
        <ElFormItem label="账号">
          <ElInput v-model="form.username" placeholder="admin" autofocus />
        </ElFormItem>
        <ElFormItem label="密码">
          <ElInput v-model="form.password" type="password" placeholder="admin123" show-password />
        </ElFormItem>
        <ElFormItem label="图形验证码">
          <div class="captcha-row">
            <ElInput v-model="form.captcha" maxlength="4" placeholder="请输入验证码" @keyup.enter="submit" />
            <button class="captcha-image" type="button" :disabled="captchaLoading" title="点击刷新验证码" @click="refreshCaptcha">
              <img v-if="captchaImage" :src="captchaImage" alt="图形验证码" />
              <span v-else>{{ captchaLoading ? '加载中' : '点击刷新' }}</span>
            </button>
          </div>
        </ElFormItem>
        <ElButton type="primary" :loading="loading" @click="submit" style="width: 100%">登录</ElButton>
      </ElForm>
      <p v-if="useMock" class="hint">Mock 账号:admin / admin123</p>
    </div>
  </div>
</template>

<style scoped>
.login-page {
  height: 100vh; display: grid; grid-template-columns: 1.15fr .85fr; align-items: center; padding: 7vw; gap: 8vw; overflow: hidden; position: relative;
  background: radial-gradient(circle at 18% 20%, rgba(52,211,153,.18), transparent 32%), linear-gradient(135deg,#103b32 0%,#0d2925 55%,#071b18 100%);
}
.login-page::after { content:'';position:absolute;width:520px;height:520px;border:1px solid rgba(255,255,255,.06);border-radius:50%;left:-140px;bottom:-260px;box-shadow:0 0 0 70px rgba(255,255,255,.025),0 0 0 140px rgba(255,255,255,.015); }
.login-brand { color:#fff; position:relative; z-index:1; }.brand-logo{display:block;width:132px;height:132px;border-radius:18px;background:#fff url('/brand/gyjk-logo.png') center/contain no-repeat;box-shadow:0 18px 46px rgba(0,0,0,.22)}.login-brand h2{font-size:42px;line-height:1.2;margin:28px 0 16px;max-width:520px}.login-brand p{max-width:530px;color:#b8d4cc;font-size:16px;line-height:1.8}.feature-list{display:flex;gap:18px;margin-top:32px;color:#d8eee7;font-size:13px;flex-wrap:wrap}.feature-list span::first-letter{color:#34d399}
.login-box {
  width: 420px; padding: 42px; background: rgba(255,255,255,.98); border:1px solid rgba(255,255,255,.35); border-radius: 20px; box-shadow: 0 28px 80px rgba(0, 0, 0, .28); position:relative;z-index:2;
}
.login-logo{display:block;width:72px;height:72px;border-radius:12px;background:#fff url('/brand/gyjk-logo.png') center/contain no-repeat;margin-bottom:22px;box-shadow:0 10px 24px rgba(15,118,110,.16)}
h1 { margin: 0 0 7px; font-size: 27px; color: var(--gyjk-text); }
.subtitle { margin: 0 0 28px; color: var(--gyjk-text-secondary); font-size: 14px; }
.login-box :deep(.el-form-item){margin-bottom:22px}.login-box :deep(.el-input__wrapper){min-height:44px}.login-box .el-button{height:44px;margin-top:4px}
.captcha-row{display:flex;gap:10px;width:100%;align-items:center}.captcha-row .el-input{flex:1}.captcha-image{flex:0 0 132px;width:132px;height:44px;padding:0;border:1px solid var(--gyjk-border);border-radius:8px;background:#eef8f4;overflow:hidden;cursor:pointer;color:var(--gyjk-text-secondary)}.captcha-image:disabled{cursor:wait;opacity:.7}.captcha-image img{display:block;width:132px;height:44px}
.hint { margin-top: 8px; color: #aaa; font-size: 12px; text-align: center; }

@media (max-width: 768px) {
  .login-page {
    grid-template-columns: 1fr;
    padding: 0;
    align-items: flex-start;
    justify-items: center;
    background: linear-gradient(135deg,#103b32 0%,#0d2925 55%,#071b18 100%);
  }
  .login-page::after { display: none; }
  .login-brand {
    display: none;
  }
  .login-box {
    width: min(420px, 92vw);
    margin-top: 10vh;
    padding: 28px 22px;
    border-radius: 16px;
  }
  .login-logo {
    width: 60px;
    height: 60px;
  }
  .login-box h1 {
    font-size: 23px;
  }
  .login-box .subtitle {
    margin-bottom: 22px;
  }
}
</style>
